/**
 * OrganizationSettingsPage - Impostazioni Organization
 * Il Diario di Bordo - SaaS
 * 
 * FASE 5: Pagina impostazioni per Responsabile con:
 * - Informazioni Organization
 * - Dati fiscali
 * - Zona Pericolosa (cancellazione)
 */

import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { organizationApi } from '../../services/organizationApi'
import { Card, PageLoader, Badge, ConfirmModal } from '../../components/ui'

import {
  Building2,
  Settings,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ArrowLeft,
  Save,
  Download,
  X,
  Clock,
  Shield,
  Receipt,
  MapPin,
  Mail,
  Phone,
  Eye,
  EyeOff,
  RefreshCw,
  AlertCircle
} from 'lucide-react'

// ============================================
// DELETION SECTION COMPONENT
// ============================================

function DeletionSection({ deletionStatus, onRequestDeletion, onCancelDeletion, loading }) {
  const [showForm, setShowForm] = useState(false)
  const [reason, setReason] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { showSuccess, showError } = useNotification()
  
  const isPendingDeletion = deletionStatus?.isPendingDeletion
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (reason.trim().length < 10) {
      showError('Il motivo deve essere di almeno 10 caratteri')
      return
    }
    
    if (!password) {
      showError('Inserisci la password per confermare')
      return
    }
    
    if (confirmText !== 'ELIMINA') {
      showError('Digita "ELIMINA" per confermare')
      return
    }
    
    setSubmitting(true)
    try {
      await onRequestDeletion(reason, password)
      setShowForm(false)
      setReason('')
      setPassword('')
      setConfirmText('')
    } finally {
      setSubmitting(false)
    }
  }
  
  const handleCancel = async () => {
    setSubmitting(true)
    try {
      await onCancelDeletion()
    } finally {
      setSubmitting(false)
    }
  }
  
  // Se cancellazione in corso
  if (isPendingDeletion) {
    const daysRemaining = deletionStatus.deletion?.daysRemaining || 0
    const scheduledAt = deletionStatus.deletion?.scheduledAt
    
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-red-100 dark:bg-red-900/50 rounded-xl flex-shrink-0">
            <Trash2 className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-xl font-bold text-red-800 dark:text-red-200">
              Cancellazione Programmata
            </h3>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Countdown */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center border border-red-200 dark:border-red-700">
                <div className="text-5xl font-bold text-red-600 dark:text-red-400">
                  {daysRemaining}
                </div>
                <div className="text-sm text-red-600 dark:text-red-400 mt-1">
                  {daysRemaining === 1 ? 'giorno rimanente' : 'giorni rimanenti'}
                </div>
              </div>
              
              {/* Data cancellazione */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-red-200 dark:border-red-700">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300 mb-2">
                  <Clock className="h-4 w-4" />
                  <span className="font-medium">Data cancellazione</span>
                </div>
                <div className="text-gray-900 dark:text-white">
                  {scheduledAt ? new Date(scheduledAt).toLocaleDateString('it-IT', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  }) : '-'}
                </div>
              </div>
              
              {/* Centri interessati */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-red-200 dark:border-red-700">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300 mb-2">
                  <Building2 className="h-4 w-4" />
                  <span className="font-medium">Centri interessati</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {deletionStatus.centersAffected || 0}
                </div>
              </div>
            </div>
            
            {/* Motivo */}
            {deletionStatus.deletion?.reason && (
              <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-700">
                <span className="text-sm font-medium text-red-700 dark:text-red-300">Motivo: </span>
                <span className="text-gray-700 dark:text-gray-300">{deletionStatus.deletion.reason}</span>
              </div>
            )}
            
            {/* Azioni */}
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={handleCancel}
                disabled={submitting}
                className="inline-flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-5 w-5 mr-2" />
                )}
                Annulla Cancellazione
              </button>
              
              <Link
                to="/organization/export"
                className="inline-flex items-center px-6 py-3 bg-white dark:bg-gray-800 border-2 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Download className="h-5 w-5 mr-2" />
                Esporta Tutti i Dati
              </Link>
            </div>
            
            {/* Warning */}
            <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">
                <strong>⚠️ Attenzione:</strong> Dopo la data di cancellazione, tutti i dati saranno eliminati permanentemente e non potranno essere recuperati.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  // Form richiesta cancellazione
  if (showForm) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg">
              <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-bold text-red-800 dark:text-red-200">
              Richiedi Cancellazione Organization
            </h3>
          </div>
          <button
            onClick={() => setShowForm(false)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Warning */}
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">
            ⚠️ Questa azione è irreversibile
          </h4>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            <li>• L'organizzazione e <strong>tutti i centri</strong> verranno eliminati</li>
            <li>• Tutti i dati dei minori e le registrazioni saranno persi</li>
            <li>• Gli account di tutti gli utenti verranno disattivati</li>
            <li>• Hai <strong>30 giorni</strong> per annullare la cancellazione</li>
          </ul>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Motivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo della cancellazione *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Spiega perché vuoi cancellare l'organizzazione (minimo 10 caratteri)"
              rows={3}
              className="input"
              required
              minLength={10}
            />
            <p className="text-xs text-gray-500 mt-1">{reason.length}/10 caratteri minimi</p>
          </div>
          
          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Inserisci la tua password per confermare"
                className="input pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          {/* Conferma */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Digita "ELIMINA" per confermare *
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="ELIMINA"
              className="input"
              required
            />
          </div>
          
          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="btn-secondary"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={submitting || reason.length < 10 || !password || confirmText !== 'ELIMINA'}
              className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Richiedi Cancellazione
            </button>
          </div>
        </form>
      </div>
    )
  }
  
  // Default: pulsante per mostrare form
  return (
    <div className="border-2 border-red-200 dark:border-red-800 rounded-xl p-6">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg flex-shrink-0">
          <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-red-700 dark:text-red-300">
            Elimina Organization
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Cancella permanentemente l'organizzazione e tutti i centri associati.
            Questa azione richiede conferma e hai 30 giorni per annullarla.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 inline-flex items-center px-4 py-2 border-2 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Richiedi Cancellazione...
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function OrganizationSettingsPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [deletionStatus, setDeletionStatus] = useState(null)
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    legalName: '',
    entityType: 'cooperativa',
    contacts: {
      email: '',
      phone: '',
      website: ''
    }
  })
  
  // Fiscal form state (separato per salvataggio indipendente)
  const [fiscalData, setFiscalData] = useState({
    fiscal: {
      partitaIva: '',
      codiceFiscale: '',
      pec: '',
      sdi: ''
    },
    legalAddress: {
      street: '',
      city: '',
      province: '',
      postalCode: ''
    }
  })
  const [savingFiscal, setSavingFiscal] = useState(false)
  
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotification()
  
  // Carica dati
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [orgRes, deletionRes] = await Promise.all([
        organizationApi.getCurrent(),
        organizationApi.getDeletionStatus().catch(() => ({ data: { data: null } }))
      ])
      
      const org = orgRes.data.organization || orgRes.data.data
      setOrganization(org)
      setDeletionStatus(deletionRes.data.data)
      
      // Popola form info
      setFormData({
        name: org?.name || '',
        legalName: org?.legalName || '',
        entityType: org?.entityType || 'cooperativa',
        contacts: {
          email: org?.contacts?.email || '',
          phone: org?.contacts?.phone || '',
          website: org?.contacts?.website || ''
        }
      })
      
      // Popola form fiscale
      setFiscalData({
        fiscal: {
          partitaIva: org?.fiscal?.partitaIva || '',
          codiceFiscale: org?.fiscal?.codiceFiscale || '',
          pec: org?.fiscal?.pec || '',
          sdi: org?.fiscal?.sdi || ''
        },
        legalAddress: {
          street: org?.legalAddress?.street || '',
          city: org?.legalAddress?.city || '',
          province: org?.legalAddress?.province || '',
          postalCode: org?.legalAddress?.postalCode || ''
        }
      })
      
    } catch (err) {
      console.error('Error loading settings:', err)
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [])
  
  useEffect(() => {
    loadData()
  }, [loadData])
  
  // Salva modifiche info
  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await organizationApi.update(formData)
      showSuccess('Impostazioni salvate')
      loadData()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }
  
  // Salva dati fiscali
  const handleSaveFiscal = async (e) => {
    e.preventDefault()
    setSavingFiscal(true)
    try {
      await organizationApi.updateFiscal(fiscalData)
      showSuccess('Dati fiscali salvati')
      loadData()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel salvataggio dati fiscali')
    } finally {
      setSavingFiscal(false)
    }
  }
  
  // Richiedi cancellazione
  const handleRequestDeletion = async (reason, password) => {
    try {
      const res = await organizationApi.requestDeletion(reason, password)
      showSuccess('Cancellazione programmata. Hai 30 giorni per annullarla.')
      loadData()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nella richiesta')
      throw err
    }
  }
  
  // Annulla cancellazione
  const handleCancelDeletion = async () => {
    try {
      await organizationApi.cancelDeletion()
      showSuccess('Cancellazione annullata!')
      loadData()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nell\'annullamento')
      throw err
    }
  }
  
  if (loading) {
    return <PageLoader />
  }
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
            Errore nel caricamento
          </h2>
          <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
          <button onClick={loadData} className="btn-primary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Riprova
          </button>
        </div>
      </div>
    )
  }
  
  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/organization"
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Impostazioni Organization
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gestisci le impostazioni della tua organizzazione
          </p>
        </div>
      </div>
      
      {/* Informazioni Organization */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Building2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Informazioni Organization
          </h2>
        </div>
        
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome Organizzazione *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
                required
              />
            </div>
            
            {/* Ragione Sociale */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ragione Sociale
              </label>
              <input
                type="text"
                value={formData.legalName}
                onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                className="input"
              />
            </div>
            
            {/* Tipo Ente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo Ente
              </label>
              <select
                value={formData.entityType}
                onChange={(e) => setFormData({ ...formData, entityType: e.target.value })}
                className="input"
              >
                <option value="cooperativa">Cooperativa</option>
                <option value="onlus">ONLUS</option>
                <option value="associazione">Associazione</option>
                <option value="fondazione">Fondazione</option>
                <option value="ente_pubblico">Ente Pubblico</option>
                <option value="altro">Altro</option>
              </select>
            </div>
            
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Contatto
              </label>
              <input
                type="email"
                value={formData.contacts.email}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  contacts: { ...formData.contacts, email: e.target.value }
                })}
                className="input"
              />
            </div>
            
            {/* Telefono */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Telefono
              </label>
              <input
                type="tel"
                value={formData.contacts.phone}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  contacts: { ...formData.contacts, phone: e.target.value }
                })}
                className="input"
              />
            </div>
            
            {/* Website */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sito Web
              </label>
              <input
                type="url"
                value={formData.contacts.website}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  contacts: { ...formData.contacts, website: e.target.value }
                })}
                placeholder="https://"
                className="input"
              />
            </div>
          </div>
          
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salva Modifiche
            </button>
          </div>
        </form>
      </Card>
      
      {/* Dati Fiscali - Editabile */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <Receipt className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Dati Fiscali e Sede Legale
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Questi dati saranno usati come default per la fatturazione di tutti i centri
            </p>
          </div>
        </div>
        
        {/* Banner cascata */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              I centri che non hanno dati fiscali propri erediteranno automaticamente questi valori per la fatturazione.
            </p>
          </div>
        </div>
        
        <form onSubmit={handleSaveFiscal} className="space-y-6">
          {/* Dati Fiscali */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Dati Fiscali
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Partita IVA
                </label>
                <input
                  type="text"
                  value={fiscalData.fiscal.partitaIva}
                  onChange={(e) => setFiscalData({
                    ...fiscalData,
                    fiscal: { ...fiscalData.fiscal, partitaIva: e.target.value.replace(/\D/g, '').slice(0, 11) }
                  })}
                  placeholder="12345678901"
                  maxLength={11}
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">11 cifre</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Codice Fiscale
                </label>
                <input
                  type="text"
                  value={fiscalData.fiscal.codiceFiscale}
                  onChange={(e) => setFiscalData({
                    ...fiscalData,
                    fiscal: { ...fiscalData.fiscal, codiceFiscale: e.target.value.toUpperCase().slice(0, 16) }
                  })}
                  placeholder="RSSMRA80A01H501U o 12345678901"
                  maxLength={16}
                  className="input uppercase"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  PEC
                </label>
                <input
                  type="email"
                  value={fiscalData.fiscal.pec}
                  onChange={(e) => setFiscalData({
                    ...fiscalData,
                    fiscal: { ...fiscalData.fiscal, pec: e.target.value }
                  })}
                  placeholder="organizzazione@pec.it"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Codice SDI
                </label>
                <input
                  type="text"
                  value={fiscalData.fiscal.sdi}
                  onChange={(e) => setFiscalData({
                    ...fiscalData,
                    fiscal: { ...fiscalData.fiscal, sdi: e.target.value.toUpperCase().slice(0, 7) }
                  })}
                  placeholder="0000000"
                  maxLength={7}
                  className="input uppercase"
                />
                <p className="text-xs text-gray-500 mt-1">7 caratteri (default: 0000000)</p>
              </div>
            </div>
          </div>
          
          {/* Sede Legale */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Sede Legale
            </h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Via/Indirizzo"
                value={fiscalData.legalAddress.street}
                onChange={(e) => setFiscalData({
                  ...fiscalData,
                  legalAddress: { ...fiscalData.legalAddress, street: e.target.value }
                })}
                className="input"
              />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2">
                  <input
                    type="text"
                    placeholder="Città"
                    value={fiscalData.legalAddress.city}
                    onChange={(e) => setFiscalData({
                      ...fiscalData,
                      legalAddress: { ...fiscalData.legalAddress, city: e.target.value }
                    })}
                    className="input"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Prov."
                    value={fiscalData.legalAddress.province}
                    onChange={(e) => setFiscalData({
                      ...fiscalData,
                      legalAddress: { ...fiscalData.legalAddress, province: e.target.value.toUpperCase() }
                    })}
                    maxLength={2}
                    className="input"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="CAP"
                    value={fiscalData.legalAddress.postalCode}
                    onChange={(e) => setFiscalData({
                      ...fiscalData,
                      legalAddress: { ...fiscalData.legalAddress, postalCode: e.target.value }
                    })}
                    maxLength={5}
                    className="input"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={savingFiscal}
              className="btn-primary"
            >
              {savingFiscal ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salva Dati Fiscali
            </button>
          </div>
        </form>
      </Card>
      
      {/* Zona Pericolosa */}
      <div className="pt-6">
        <h2 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Zona Pericolosa
        </h2>
        
        <DeletionSection
          deletionStatus={deletionStatus}
          onRequestDeletion={handleRequestDeletion}
          onCancelDeletion={handleCancelDeletion}
          loading={loading}
        />
      </div>
    </div>
  )
}
